import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const PricePredictionHistoryPage = () => {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [symbol, setSymbol] = useState('');
  const [expandedPrediction, setExpandedPrediction] = useState(null);

  const fetchPredictions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      
      if (symbol) params.append('symbol', symbol.toUpperCase());
      
      const response = await api.get(`/api/v1/tools/price-prediction-history?${params.toString()}`);
      
      setPredictions(response.data.predictions || []);
      setTotalCount(response.data.pagination?.total_count || 0);
      setTotalPages(response.data.pagination?.total_pages || 0);
    } catch (err) {
      console.error('Error fetching prediction history:', err);
      setError(err.response?.data?.detail || 'Failed to fetch prediction history');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, symbol]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  const handleResetFilters = () => {
    setSymbol('');
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${parseFloat(value).toFixed(2)}%`;
  };

  const getDirectionColor = (direction) => {
    return direction === 'bullish' 
      ? 'text-green-600 bg-green-50' 
      : 'text-red-600 bg-red-50';
  };

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'Accurate':
        return 'text-green-600 bg-green-50';
      case 'Inaccurate':
        return 'text-red-600 bg-red-50';
      case 'Pending':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const toggleExpand = (predictionId) => {
    setExpandedPrediction(expandedPrediction === predictionId ? null : predictionId);
  };

  if (loading && predictions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading prediction history...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Price Prediction History</h1>
          <p className="text-gray-600">View all your price predictions and track their accuracy</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Symbol</label>
              <input
                type="text"
                value={symbol}
                onChange={(e) => { setSymbol(e.target.value.toUpperCase()); setPage(1); }}
                placeholder="e.g., NIFTY, RELIANCE"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Predictions List */}
        {predictions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-600">No predictions found. Make your first prediction to see it here!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {predictions.map((pred) => (
              <div key={pred.prediction_id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpand(pred.prediction_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{pred.symbol}</h3>
                        <p className="text-sm text-gray-500">{formatDate(pred.timestamp)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getDirectionColor(pred.prediction_direction)}`}>
                          {pred.prediction_direction === 'bullish' ? '📈 Bullish' : '📉 Bearish'}
                        </span>
                        {pred.outcome && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getOutcomeColor(pred.outcome)}`}>
                            {pred.outcome}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Predicted Change</p>
                      <p className={`text-lg font-semibold ${pred.predicted_change_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercentage(pred.predicted_change_pct)}
                      </p>
                    </div>
                    <div className="text-gray-400">
                      {expandedPrediction === pred.prediction_id ? '▼' : '▶'}
                    </div>
                  </div>
                </div>

                {expandedPrediction === pred.prediction_id && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Current Price</p>
                        <p className="text-lg font-semibold text-gray-900">{formatPrice(pred.current_price)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Predicted Price</p>
                        <p className={`text-lg font-semibold ${pred.prediction_direction === 'bullish' ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPrice(pred.predicted_price)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Prediction Horizon</p>
                        <p className="text-lg font-semibold text-gray-900">{pred.prediction_horizon} day(s)</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Historical Data Period</p>
                        <p className="text-lg font-semibold text-gray-900">{pred.period_years} year(s)</p>
                      </div>
                      {pred.confidence_interval_lower && pred.confidence_interval_upper && (
                        <>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Confidence Lower</p>
                            <p className="text-lg font-semibold text-gray-900">{formatPrice(pred.confidence_interval_lower)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Confidence Upper</p>
                            <p className="text-lg font-semibold text-gray-900">{formatPrice(pred.confidence_interval_upper)}</p>
                          </div>
                        </>
                      )}
                      {pred.test_r2 !== null && pred.test_r2 !== undefined && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Model R² Score</p>
                          <p className="text-lg font-semibold text-gray-900">{(pred.test_r2 * 100).toFixed(2)}%</p>
                        </div>
                      )}
                      {pred.test_mae_pct !== null && pred.test_mae_pct !== undefined && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Test MAE</p>
                          <p className="text-lg font-semibold text-gray-900">{formatPercentage(pred.test_mae_pct)}</p>
                        </div>
                      )}
                      {pred.feature_count && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Features Used</p>
                          <p className="text-lg font-semibold text-gray-900">{pred.feature_count}</p>
                        </div>
                      )}
                      {pred.actual_price && (
                        <>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Actual Price</p>
                            <p className="text-lg font-semibold text-gray-900">{formatPrice(pred.actual_price)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Actual Change</p>
                            <p className={`text-lg font-semibold ${pred.actual_change_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPercentage(pred.actual_change_pct)}
                            </p>
                          </div>
                          {pred.accuracy_pct !== null && pred.accuracy_pct !== undefined && (
                            <div>
                              <p className="text-sm text-gray-600 mb-1">Accuracy</p>
                              <p className="text-lg font-semibold text-gray-900">{pred.accuracy_pct.toFixed(2)}%</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Top Features */}
                    {pred.top_features && pred.top_features.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">Top Contributing Features</p>
                        <div className="flex flex-wrap gap-2">
                          {pred.top_features.slice(0, 5).map((feature, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                              {feature.name}: {(feature.importance * 100).toFixed(1)}%
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} predictions
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default PricePredictionHistoryPage;

